import {HttpClient}         from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {BaseService}        from '@src/app/shared/services/base.service';
import {Observable}         from 'rxjs';

export type BillingPlanSelection = 'payg' | 'pro_monthly' | 'pro_annual';
export type CheckoutState = 'open' | 'pending' | 'succeeded' | 'failed' | 'cancelled';

export interface CheckoutSession
{
    state: CheckoutState;
    fulfilled: boolean;
    url?: string | null;
}

export interface BillingOffer
{
    id: string;
    kind: 'credit_pack' | 'base' | 'seat';
    plan: string;
    price_id: string;
    name?: string;
    amount: number;
    currency: string;
    interval?: 'month' | 'year' | null;
    tax_behavior?: string;
    credit_quantity?: number;
    included_surveyors?: number;
    document_storage_gb?: number;
    feature_keys?: string[];
}

export interface BillingCatalogue
{
    version: string;
    status: string;
    offers: BillingOffer[];
    effective_from?: string | null;
    grandfathering_ends_at?: string | null;
    usage?: {
        effective_from: string;
        effective_until: string | null;
        uses_credits: boolean;
        credit_weights: Record<string, number | null> | null;
    } | null;
}

export interface SurveyorBillingEstimate
{
    status: 'ready' | 'setup_required' | 'not_applicable';
    estimated: boolean;
    calculated_at: string;
    active_surveyors: number;
    proposed_surveyors: number;
    included_surveyors: number | null;
    extra_surveyors: number | null;
    extra_capacity: number | null;
    available_extra_seats: number | null;
    reduction_at_renewal: number | null;
    monthly_unit_amount: number | null;
    accrued_amount: number | null;
    projected_amount: number | null;
    incremental_amount: number | null;
    ongoing_monthly_amount: number | null;
    ongoing_monthly_change?: number | null;
    next_billing_date: string | null;
    currency?: string;
    tax_behavior?: string;
    tax_included_in_estimate?: boolean;
    discounts_included_in_estimate?: boolean;
    period?: { starts_at: string; ends_at: string } | null;
    base?: { amount: number; interval: 'month' | 'year'; paid_until?: string | null } | null;
    base_ends_at?: string | null;
    extras_ends_at?: string | null;
    chargeable_activations_blocked: boolean;
    payment_grace_ends_at?: string | null;
    payment_grace_expired?: boolean;
    payment_failure_time_unknown?: boolean;
    staff_follow_up: boolean;
    total_licences?: number;
    assigned_licences?: number;
    available_licences?: number;
    included_licences?: number;
    purchased_licences?: number;
    transitional_licences?: number;
    cancellation_pending?: number;
    licence_required?: boolean;
}

export interface SurveyorLicence
{
    id: string;
    number: number | null;
    identifier: string | null;
    source: 'included' | 'purchased' | 'transitional';
    status: 'active' | 'cancellation_pending';
    created_at: string;
    renews_at?: string | null;
    cancels_at?: string | null;
    user?: SurveyorLicenceUser | null;
}

export interface SurveyorLicenceUser
{
    id: number;
    name: string;
    email: string;
    access_mode: 'none' | 'web' | 'app' | 'both';
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService extends BaseService
{
    private readonly http = inject(HttpClient);

    public checkout(data): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments`, data);
    }

    public getCatalogue(): Observable<BillingCatalogue>
    {
        return this.http.get<BillingCatalogue>(`${this.apiUrl}/payments/catalogue`);
    }

    public getPlans(): Observable<any>
    {
        return this.http.get(`${this.apiUrl}/payments/plans`);
    }

    public getPlan(): Observable<any>
    {
        return this.http.get(`${this.apiUrl}/payments/plan`);
    }

    public selectPlan(plan: BillingPlanSelection): Observable<any>
    {
        return this.http.put(`${this.apiUrl}/payments/plan`, {plan});
    }

    public cancelSubscription(): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/subscriptions/cancel`, {});
    }

    public cancelSubscriptionNow(): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/subscriptions/cancel-now`, {});
    }

    public resumeSubscription(): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/subscriptions/resume`, {});
    }

    public getBillingPreview(): Observable<{data: BillingInvoicePreview[]}>
    {
        return this.http.get<{data: BillingInvoicePreview[]}>(`${this.apiUrl}/payments/billing-preview`);
    }

    public getSurveyorBilling(): Observable<SurveyorBillingEstimate>
    {
        return this.http.get<SurveyorBillingEstimate>(`${this.apiUrl}/payments/surveyors`);
    }

    public getSurveyorLicences(): Observable<{ data: SurveyorLicence[]; assignable_users: SurveyorLicenceUser[] }>
    {
        return this.http.get<{
            data: SurveyorLicence[];
            assignable_users: SurveyorLicenceUser[]
        }>(`${this.apiUrl}/payments/surveyor-licences`);
    }

    public assignSurveyorLicence(id: string, userId: number): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/surveyor-licences/${id}/assign`, {user_id: userId});
    }

    public unassignSurveyorLicence(id: string): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/surveyor-licences/${id}/unassign`, {});
    }

    public cancelSurveyorLicence(id: string): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/surveyor-licences/${id}/cancel`, {});
    }

    public resumeSurveyorLicence(id: string): Observable<any>
    {
        return this.http.post(`${this.apiUrl}/payments/surveyor-licences/${id}/resume`, {});
    }

    public previewSurveyorChange(proposal: Record<string, unknown>): Observable<SurveyorBillingEstimate>
    {
        return this.http.post<SurveyorBillingEstimate>(`${this.apiUrl}/payments/surveyors/preview`, proposal);
    }

    public getCustomerPortal(): Observable<any>
    {
        return this.http.get(`${this.apiUrl}/payments/billing-portal`, {
            params: {
                frontend_url: window.location.href
            }
        });
    }

    public checkSession(session_id: string): Observable<CheckoutSession>
    {
        return this.http.get<CheckoutSession>(`${this.apiUrl}/payments`, {
            params: {
                session_id: session_id
            }
        });
    }
}

export interface BillingInvoicePreview {
    label: string;
    currency: string;
    discount_amount: number;
    amount_due: number;
    billing_at: string | null;
}
